import { Record, RecordOf } from 'immutable';
import { Action } from 'redux/types';
import { getActionTypes } from './actions';
import { UploadStatus } from './constants';
import { ActionTypes, EntireVideoState, State, WrappedState } from './types';

export const stateFactory = Record<State>({
  entireVideoId: undefined,
  entireUploadedVideo: undefined,
  uploadStatus: undefined,
  isUploadingEntireVideo: false,
  isResolvingAudioPeaks: false,
  audioPeaks: undefined,
});

export default function createReducer<
  S extends EntireVideoState,
  A extends Action<string, any>
>(
  originalReducer: (state: WrappedState<S>, action: A) => WrappedState<S>,
  underlyingDefaultState: WrappedState<S>,
  actionTypesOrName: ActionTypes | string,
) {
  const actionTypes = getActionTypes(actionTypesOrName);
  const defaultState = underlyingDefaultState.set(
    'entireVideo',
    stateFactory() as RecordOf<State>,
  );

  const reducer = (state: RecordOf<State>, action: A) => {
    if (action.type === actionTypes.transferRequest) {
      return state.withMutations(s => {
        s.set('isUploadingEntireVideo', true);
        s.set('uploadStatus', UploadStatus.UPLOADING);

        return s;
      });
    }

    if (action.type === actionTypes.transferSuccess) {
      const { entireVideoId, entireUploadedVideo } = action.payload;

      return state.withMutations(s => {
        s.set('isUploadingEntireVideo', false);
        s.set('entireVideoId', entireVideoId);
        s.set('entireUploadedVideo', entireUploadedVideo);
        s.set('uploadStatus', UploadStatus.UPLOADED);

        return s;
      });
    }

    if (action.type === actionTypes.transferFailure) {
      return state.withMutations(s => {
        s.set('isUploadingEntireVideo', false);
        s.set('uploadStatus', UploadStatus.FAILED);

        return s;
      });
    }

    if (action.type === actionTypes.resolveAudioPeaksRequest) {
      return state.withMutations(s => {
        s.set('isResolvingAudioPeaks', true);

        return s;
      });
    }

    if (action.type === actionTypes.resolveAudioPeaksSuccess) {
      const { audioPeaks } = action.payload;

      return state.withMutations(s => {
        s.set('audioPeaks', audioPeaks);
        s.set('isResolvingAudioPeaks', false);

        return s;
      });
    }

    if (action.type === actionTypes.resolveAudioPeaksFailure) {
      return state.withMutations(s => {
        s.set('isResolvingAudioPeaks', false);

        return s;
      });
    }

    if (action.type === actionTypes.clear) {
      return stateFactory() as RecordOf<State>;
    }

    return state;
  };

  return (state: any = defaultState, action: A) => {
    const entireVideoState = state.get('entireVideo') as RecordOf<State>;
    const newUnderlyingState = originalReducer(state || defaultState, action);
    const newEntireVideoState = reducer(entireVideoState, action);

    return newUnderlyingState.set('entireVideo', newEntireVideoState);
  };
}
